import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Mail, Lock, User as UserIcon, Building2, Phone, AlertCircle, Loader2 } from 'lucide-react';
import {
  registerUser,
  selectAuthStatus,
  selectAuthError,
  selectAuthProviders,
  selectIsAuthenticated,
} from '../store/slices/authSlice';
import { startGoogleOAuth } from '../services/api';

/**
 * Sign-Up page.
 *
 * Same design system anchors as SignInPage. Adds optional company/phone
 * fields because the primary audience (contractors and wholesale buyers)
 * benefits from the sales team having that context attached to RFQs.
 */
export default function SignUpPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const status = useSelector(selectAuthStatus);
  const error = useSelector(selectAuthError);
  const providers = useSelector(selectAuthProviders);
  const isAuth = useSelector(selectIsAuthenticated);
  const fromParam = searchParams.get('from');

  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    company: '',
    phone: '',
  });
  const [localError, setLocalError] = useState(null);

  useEffect(() => {
    if (isAuth) {
      const target = fromParam ? decodeURIComponent(fromParam) : '/';
      navigate(target, { replace: true });
    }
  }, [isAuth, navigate, fromParam]);

  const handleChange = (e) => {
    setLocalError(null);
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      setLocalError('Password must be at least 6 characters.');
      return;
    }
    dispatch(registerUser(form));
  };

  const showGoogle =
    providers.google && process.env.REACT_APP_GOOGLE_OAUTH_VISIBLE !== 'false';
  const isLoading = status === 'loading';
  const displayError = localError || error;

  return (
    <main className="min-h-screen bg-cream-100 pt-24 md:pt-32 pb-24">
      <div className="max-w-md mx-auto px-6">
        <p className="section-label text-center mb-3">Create your account</p>

        <h1 className="font-serif text-4xl md:text-5xl text-charcoal text-center leading-tight tracking-tight">
          Get started
        </h1>

        <p className="font-sans text-sm text-warm-stone text-center mt-3 mb-10 max-w-sm mx-auto">
          Save quote requests, track project history, and check out faster.
        </p>

        <div className="bg-white p-7 md:p-9 border border-gray-100 shadow-sm">
          {displayError && (
            <div className="flex items-start gap-2 mb-5 px-3 py-2.5 border border-red-100 bg-red-50">
              <AlertCircle size={14} className="text-red-600 mt-0.5 flex-shrink-0" />
              <p className="font-sans text-xs text-red-700">{displayError}</p>
            </div>
          )}

          {showGoogle && (
            <>
              <button
                type="button"
                onClick={startGoogleOAuth}
                className="w-full flex items-center justify-center gap-3 border border-charcoal text-charcoal font-sans text-sm tracking-wider uppercase py-3 hover:bg-charcoal hover:text-white transition-colors"
              >
                <GoogleMark />
                Sign up with Google
              </button>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-100" />
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white px-3 font-sans text-[10px] tracking-[0.25em] uppercase text-warm-stone">
                    or
                  </span>
                </div>
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            <Field
              icon={<UserIcon size={15} />}
              label="Full name"
              name="name"
              value={form.name}
              onChange={handleChange}
              autoComplete="name"
              required
            />
            <Field
              icon={<Mail size={15} />}
              label="Email"
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              autoComplete="email"
              required
            />
            <Field
              icon={<Lock size={15} />}
              label="Password"
              type="password"
              name="password"
              value={form.password}
              onChange={handleChange}
              autoComplete="new-password"
              minLength={6}
              required
              helper="At least 6 characters"
            />
            <Field
              icon={<Building2 size={15} />}
              label="Company (optional)"
              name="company"
              value={form.company}
              onChange={handleChange}
              autoComplete="organization"
            />
            <Field
              icon={<Phone size={15} />}
              label="Phone (optional)"
              type="tel"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              autoComplete="tel"
            />

            <button
              type="submit"
              disabled={isLoading || !form.name || !form.email || !form.password}
              className="w-full flex items-center justify-center gap-2 bg-gold text-charcoal font-sans text-sm tracking-widest uppercase font-medium py-3.5 hover:bg-charcoal hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader2 size={15} className="animate-spin" />
                  Creating account
                </>
              ) : (
                'Create account'
              )}
            </button>

            <p className="font-sans text-[11px] text-warm-stone leading-relaxed">
              By creating an account you agree to our terms and our cookie
              policy. Your data is used to manage quotes, orders, and
              communication only.
            </p>
          </form>

          <p className="font-sans text-xs text-warm-stone text-center mt-7">
            Already have an account?{' '}
            <Link
              to={fromParam ? `/signin?from=${fromParam}` : '/signin'}
              className="text-charcoal underline-offset-2 hover:text-gold hover:underline transition-colors"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </main>
  );
}

function Field({ icon, label, helper, ...inputProps }) {
  return (
    <label className="block">
      <span className="block font-sans text-[11px] tracking-widest uppercase text-warm-stone font-medium mb-1.5">
        {label}
        {inputProps.required && <span className="text-gold ml-0.5">*</span>}
      </span>
      <span className="relative block">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-warm-stone pointer-events-none">
          {icon}
        </span>
        <input
          {...inputProps}
          className="w-full h-11 pl-10 pr-3 border border-gray-200 bg-white font-sans text-sm text-charcoal placeholder-warm-stone focus:border-gold focus:ring-2 focus:ring-gold/20 focus:outline-none transition-colors"
          style={{ borderRadius: '2px' }}
        />
      </span>
      {helper && (
        <span className="block font-sans text-[11px] text-warm-stone mt-1">
          {helper}
        </span>
      )}
    </label>
  );
}

function GoogleMark() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.98.66-2.24 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.17v2.84A11 11 0 0 0 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.11A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.34-2.11V7.05H2.17A11 11 0 0 0 1 12c0 1.78.43 3.46 1.17 4.95l3.67-2.84z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.17 7.05l3.67 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
    </svg>
  );
}
